import { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion, useReducedMotion } from 'framer-motion';
import { staggerContainer, fadeUp, reducedMotionVariants } from '../../../theme/animations';
import { SectionTitle, SectionSubtitle } from './SectionHeader';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.paper,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px !important',
  marginBottom: theme.spacing(2),
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[4],
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(0, 3),
  minHeight: 64,
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(2, 0),
  },
  '&.Mui-expanded': {
    minHeight: 64,
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  lineHeight: 1.5,
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 3, 3),
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
}));

export interface FaqItem {
  question: string;
  answer: React.ReactNode;
  /** Plain-text version of the answer for JSON-LD (optional, falls back to string coercion) */
  answerText?: string;
}

interface FAQSectionInnerProps {
  faqs: FaqItem[];
}

function FAQSectionInner({ faqs }: FAQSectionInnerProps) {
  const [expanded, setExpanded] = useState<string | false>(false);
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <SectionContainer id="faq">
      <Container maxWidth="md">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={itemVariants}>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <SectionSubtitle>
              Everything you need to know about Lord of Venues. Can&apos;t find your answer? Reach
              out to our support team.
            </SectionSubtitle>
          </motion.div>

          <motion.div variants={itemVariants}>
            {faqs.map((faq, index) => {
              const panelId = `faq-panel-${index}`;
              return (
                <StyledAccordion
                  key={panelId}
                  expanded={expanded === panelId}
                  onChange={handleChange(panelId)}
                  disableGutters
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                    aria-controls={`${panelId}-content`}
                    id={`${panelId}-header`}
                  >
                    <QuestionText>{faq.question}</QuestionText>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    <AnswerText {...({ component: 'div' } as object)}>{faq.answer}</AnswerText>
                  </StyledAccordionDetails>
                </StyledAccordion>
              );
            })}
          </motion.div>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}

interface FAQSectionProps {
  faqs: FaqItem[];
}

export function FAQSection({ faqs }: FAQSectionProps) {
  return <FAQSectionInner faqs={faqs} />;
}
